// ══════════════════════════════════════════════════════════════════════════════
// PujaRoom.js — Live Interactive Puja Video Call · VedSanga · Agora WebRTC
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import AgoraRTC, {
  AgoraRTCProvider,
  LocalVideoTrack,
  RemoteUser,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteUsers,
  useRTCClient,
} from "agora-rtc-react";

const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
const API    = process.env.REACT_APP_API_URL || "http://localhost:8000";

// ── Ritual moment cards — native language meanings shown on devotee screen ─────
const RITUAL_MOMENTS = {
  invocation: { emoji: "🙏", title: { en:"Invocation", hi:"आह्वान", te:"ఆహ్వానం", ta:"ஆவாஹனம்", kn:"ಆಹ್ವಾನ" },
    text: { en:"Inviting the deity to be present — the sacred space is now open",
            hi:"देवता का आह्वान — अब यह स्थान पवित्र हो गया है",
            te:"దేవతను ఆహ్వానిస్తున్నారు — ఈ పవిత్ర స్థలం ఇప్పుడు తెరుచుకుంది",
            ta:"தெய்வத்தை அழைக்கிறோம் — இப்போது இந்த இடம் புனிதமானது",
            kn:"ದೇವರನ್ನು ಆಹ್ವಾನಿಸಲಾಗುತ್ತಿದೆ — ಈ ಪವಿತ್ರ ಸ್ಥಳ ಈಗ ತೆರೆದಿದೆ" }},
  kalash:     { emoji: "🏺", title: { en:"Kalash", hi:"कलश", te:"కలశం", ta:"கலசம்", kn:"ಕಲಶ" },
    text: { en:"The sacred pot is established — all holy rivers and divine energies are present here",
            hi:"कलश स्थापित हो गया — सभी पवित्र नदियाँ और देवशक्तियाँ यहाँ उपस्थित हैं",
            te:"కలశం స్థాపించబడింది — అన్ని పవిత్ర నదులు మరియు దివ్యశక్తులు ఇప్పుడు ఇక్కడ ఉన్నాయి",
            ta:"கலசம் அமைக்கப்பட்டது — அனைத்து புனித நதிகளும் தெய்வீக சக்திகளும் இங்கே இருக்கின்றன",
            kn:"ಕಲಶ ಸ್ಥಾಪಿಸಲಾಗಿದೆ — ಎಲ್ಲಾ ಪವಿತ್ರ ನದಿಗಳು ಮತ್ತು ದೈವಶಕ್ತಿಗಳು ಇಲ್ಲಿ ಈಗ ಇವೆ" }},
  ganesh:     { emoji: "🐘", title: { en:"Ganesh Puja", hi:"गणेश पूजा", te:"గణేష పూజ", ta:"கணேஷ் பூஜை", kn:"ಗಣೇಶ ಪೂಜೆ" },
    text: { en:"Ganesha is worshipped first — removing all obstacles before your intention is offered",
            hi:"पहले गणेशजी की पूजा — आपके संकल्प से पहले सभी विघ्न दूर किए जा रहे हैं",
            te:"ముందుగా గణేశుడిని పూజిస్తున్నారు — మీ సంకల్పం అర్పించే ముందు అన్ని విఘ్నాలు తొలగించబడుతున్నాయి",
            ta:"முதலில் கணேஷ் வழிபாடு — உங்கள் நோக்கம் அர்ப்பணிக்கப்படுவதற்கு முன் தடைகள் நீக்கப்படுகின்றன",
            kn:"ಮೊದಲು ಗಣೇಶನ ಪೂಜೆ — ನಿಮ್ಮ ಸಂಕಲ್ಪ ಅರ್ಪಿಸುವ ಮೊದಲು ಎಲ್ಲಾ ವಿಘ್ನಗಳು ನಿವಾರಣೆಯಾಗುತ್ತಿವೆ" }},
  offerings:  { emoji: "🌸", title: { en:"Offerings", hi:"अर्पण", te:"నైవేద్యం", ta:"நிவேதனம்", kn:"ನೈವೇದ್ಯ" },
    text: { en:"Sacred offerings are being made — flowers, water, incense, light — each one carries your devotion to the deity",
            hi:"पवित्र अर्पण किए जा रहे हैं — फूल, जल, धूप, दीप — हर एक में आपकी भक्ति देवता तक पहुँचती है",
            te:"పవిత్ర నైవేద్యాలు సమర్పించబడుతున్నాయి — పూలు, నీళ్ళు, ధూపం, దీపం — ప్రతిదీ మీ భక్తిని దేవతకు చేర్చుతోంది",
            ta:"புனித காணிக்கைகள் செலுத்தப்படுகின்றன — மலர்கள், நீர், தூபம், விளக்கு — ஒவ்வொன்றும் உங்கள் பக்தியை தெய்வத்திடம் சேர்க்கிறது",
            kn:"ಪವಿತ್ರ ನೈವೇದ್ಯಗಳನ್ನು ಅರ್ಪಿಸಲಾಗುತ್ತಿದೆ — ಹೂವು, ನೀರು, ಧೂಪ, ದೀಪ — ಪ್ರತಿಯೊಂದೂ ನಿಮ್ಮ ಭಕ್ತಿಯನ್ನು ದೇವರಿಗೆ ತಲುಪಿಸುತ್ತದೆ" }},
  mantra:     { emoji: "📿", title: { en:"Main Mantra", hi:"मुख्य मंत्र", te:"ముఖ్య మంత్రం", ta:"முக்கிய மந்திரம்", kn:"ಮುಖ್ಯ ಮಂತ್ರ" },
    text: { en:"The core mantras are being chanted — your name and intention are woven into every word",
            hi:"मुख्य मंत्र जप हो रहा है — आपका नाम और संकल्प हर शब्द में गूँज रहा है",
            te:"ముఖ్యమైన మంత్రాలు జపించబడుతున్నాయి — ప్రతి మాటలో మీ పేరు మరియు సంకల్పం ప్రతిధ్వనిస్తోంది",
            ta:"முக்கிய மந்திரங்கள் உச்சரிக்கப்படுகின்றன — ஒவ்வொரு வார்த்தையிலும் உங்கள் பெயரும் நோக்கமும் ஒலிக்கின்றன",
            kn:"ಮುಖ್ಯ ಮಂತ್ರಗಳನ್ನು ಜಪಿಸಲಾಗುತ್ತಿದೆ — ಪ್ರತಿ ಪದದಲ್ಲೂ ನಿಮ್ಮ ಹೆಸರು ಮತ್ತು ಸಂಕಲ್ಪ ಅನುರಣಿಸುತ್ತಿದೆ" }},
  homam:      { emoji: "🔥", title: { en:"Homam / Fire", hi:"हवन", te:"హోమం", ta:"ஹோமம்", kn:"ಹೋಮ" },
    text: { en:"Offerings are made to the sacred fire — what you give, the fire purifies and carries directly to the divine",
            hi:"पवित्र अग्नि में आहुति दी जा रही है — आप जो अर्पण करते हैं, अग्नि उसे शुद्ध करके सीधे देवता तक पहुँचाती है",
            te:"పవిత్ర అగ్నిలో ఆహుతి అర్పించబడుతోంది — మీరు ఇచ్చేది అగ్ని శుద్ధి చేసి నేరుగా దేవతకు చేరుస్తుంది",
            ta:"புனித நெருப்பில் ஆகுதி செலுத்தப்படுகிறது — நீங்கள் தரும் தியாகத்தை அக்னி தூய்மைப்படுத்தி தெய்வத்திடம் கொண்டு செல்கிறது",
            kn:"ಪವಿತ್ರ ಅಗ್ನಿಗೆ ಆಹುತಿ ನೀಡಲಾಗುತ್ತಿದೆ — ನೀವು ನೀಡುವುದನ್ನು ಅಗ್ನಿ ಶುದ್ಧಿಗೊಳಿಸಿ ನೇರವಾಗಿ ದೇವರಿಗೆ ತಲುಪಿಸುತ್ತದೆ" }},
  blessing:   { emoji: "✨", title: { en:"Final Blessing", hi:"अंतिम आशीर्वाद", te:"తుది దీవెన", ta:"இறுதி ஆசி", kn:"ಅಂತಿಮ ಆಶೀರ್ವಾದ" },
    text: { en:"The puja is complete — your offering is received and your sankalpa is now sealed with divine grace",
            hi:"पूजा पूर्ण हुई — आपका अर्पण स्वीकार हुआ और आपका संकल्प दिव्य कृपा से अभिषिक्त हो गया",
            te:"పూజ పూర్తయింది — మీ నైవేద్యం స్వీకరించబడింది మరియు మీ సంకల్పం దివ్యానుగ్రహంతో ముద్రించబడింది",
            ta:"பூஜை நிறைவடைந்தது — உங்கள் காணிக்கை ஏற்கப்பட்டது, உங்கள் சங்கல்பம் தெய்வீக அருளால் முத்திரையிடப்பட்டது",
            kn:"ಪೂಜೆ ಮುಗಿಯಿತು — ನಿಮ್ಮ ನೈವೇದ್ಯ ಸ್ವೀಕರಿಸಲ್ಪಟ್ಟಿದೆ ಮತ್ತು ನಿಮ್ಮ ಸಂಕಲ್ಪ ದಿವ್ಯ ಕೃಪೆಯಿಂದ ಮುದ್ರಿಸಲ್ಪಟ್ಟಿದೆ" }},
};

// Detect user's preferred language from browser/localStorage
const detectLang = () => {
  const stored = localStorage.getItem("vedsanga_lang");
  if (stored && RITUAL_MOMENTS.invocation.text[stored]) return stored;
  const nav = (navigator.language || "en").toLowerCase();
  if (nav.startsWith("te")) return "te";
  if (nav.startsWith("hi")) return "hi";
  if (nav.startsWith("ta")) return "ta";
  if (nav.startsWith("kn")) return "kn";
  return "en";
};

// ── Design tokens (mirror App.js) ─────────────────────────────────────────────
const C = {
  bg: "#06030A", bg2: "#0D0818", bg3: "#130D22",
  saffron: "#E8832A", gold: "#C9953A", cream: "#FFF5E0",
  text: "#EDE0C8", dim: "#8A7558",
  border: "rgba(201,149,58,0.25)",
};

// ── Loading / Connecting screen ────────────────────────────────────────────────
function LoadingRoom({ pujaName, pujaEmoji }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: C.bg,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", zIndex: 1000,
    }}>
      <style>{`
        @keyframes dot-pulse {
          0%   { opacity: 0.3; transform: scale(0.7); }
          100% { opacity: 1;   transform: scale(1.3); }
        }
      `}</style>
      <div style={{ fontSize: "4rem", marginBottom: "16px", filter: "drop-shadow(0 0 20px rgba(232,131,42,0.5))" }}>
        {pujaEmoji || "🙏"}
      </div>
      <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "1.1rem", color: C.saffron, marginBottom: "6px" }}>
        {pujaName}
      </div>
      <div style={{ fontSize: "0.85rem", color: C.dim, marginBottom: "24px" }}>
        Entering sacred space…
      </div>
      <div style={{ display: "flex", gap: "8px" }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: "10px", height: "10px", borderRadius: "50%",
            background: C.saffron,
            animation: `dot-pulse 0.7s ${i * 0.15}s ease-in-out infinite alternate`,
          }} />
        ))}
      </div>
    </div>
  );
}

// ── Single video tile ──────────────────────────────────────────────────────────
function VideoTile({ remoteUser, localCameraTrack, name, isPriest, isMuted, isCamOff, isLocal, style }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative", borderRadius: "14px", overflow: "hidden",
        background: C.bg2,
        border: `1px solid ${isPriest
          ? (hov ? "rgba(232,131,42,0.7)" : "rgba(232,131,42,0.4)")
          : (hov ? "rgba(201,149,58,0.4)" : C.border)}`,
        boxShadow: isPriest ? "0 0 24px rgba(232,131,42,0.12)" : "none",
        transition: "all 0.2s",
        ...style,
      }}>

      {/* Video stream or avatar */}
      {isLocal && localCameraTrack && !isCamOff ? (
        <LocalVideoTrack track={localCameraTrack} play={true} style={{ width: "100%", height: "100%" }} />
      ) : !isLocal && remoteUser && remoteUser.hasVideo ? (
        <RemoteUser user={remoteUser} playVideo={true} playAudio={true} style={{ width: "100%", height: "100%" }} />
      ) : (
        <div style={{
          width: "100%", height: "100%",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: "10px",
        }}>
          <div style={{
            width: "68px", height: "68px", borderRadius: "50%",
            background: isPriest ? "rgba(232,131,42,0.15)" : "rgba(201,149,58,0.08)",
            border: `2px solid ${isPriest ? "rgba(232,131,42,0.4)" : C.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "2rem",
          }}>
            {isPriest ? "🔱" : "🙏"}
          </div>
          <div style={{ fontSize: "0.72rem", color: C.dim }}>
            {isCamOff ? "Camera off" : "Connecting…"}
          </div>
        </div>
      )}

      {/* Remote audio (if not via RemoteUser) */}
      {!isLocal && remoteUser && !remoteUser.hasVideo && remoteUser.hasAudio && (
        <RemoteUser user={remoteUser} playVideo={false} playAudio={true}
          style={{ position: "absolute", width: 0, height: 0, opacity: 0 }} />
      )}

      {/* Gradient overlay + name bar */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        background: "linear-gradient(transparent, rgba(6,3,10,0.88))",
        padding: "28px 12px 10px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "7px", minWidth: 0 }}>
          {isPriest && (
            <span style={{
              background: "rgba(232,131,42,0.2)", border: "1px solid rgba(232,131,42,0.4)",
              borderRadius: "6px", padding: "1px 7px",
              fontSize: "0.6rem", color: C.saffron,
              fontFamily: "'Cinzel Decorative', serif", letterSpacing: "0.05em",
              whiteSpace: "nowrap",
            }}>🔱 PRIEST</span>
          )}
          <span style={{
            fontSize: "0.82rem", color: C.cream,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{name}</span>
          {isLocal && <span style={{ fontSize: "0.65rem", color: C.dim, whiteSpace: "nowrap" }}>(You)</span>}
        </div>
        <div style={{ display: "flex", gap: "5px", flexShrink: 0 }}>
          {isMuted   && <span title="Muted">🔇</span>}
          {isCamOff  && <span title="Camera off">📵</span>}
        </div>
      </div>
    </div>
  );
}

// ── Bottom control button ──────────────────────────────────────────────────────
function CtrlBtn({ icon, label, active, danger, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", gap: "5px",
        background: danger
          ? (hov ? "rgba(239,68,68,0.35)" : "rgba(239,68,68,0.18)")
          : (active ? "rgba(232,131,42,0.18)" : (hov ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)")),
        border: `1px solid ${danger
          ? "rgba(239,68,68,0.45)"
          : (active ? "rgba(232,131,42,0.4)" : "rgba(255,255,255,0.1)")}`,
        borderRadius: "12px", padding: "11px 20px", cursor: "pointer",
        transition: "all 0.2s", minWidth: "76px",
      }}>
      <span style={{ fontSize: "1.5rem" }}>{icon}</span>
      <span style={{ fontSize: "0.66rem", color: danger ? "#fca5a5" : C.dim }}>
        {label}
      </span>
    </button>
  );
}

// ── Room inner — all Agora hooks live here (must be inside AgoraRTCProvider) ──
function RoomInner({ booking, agoraConfig, onLeave }) {
  const { appId, token, channel, uid, priestUid, monitorUid, priestName, priestUpi,
          deviceNum = "1", bookingName = "" } = agoraConfig;
  const isPriestLocal = uid === priestUid;
  const isMonitor     = !isPriestLocal && deviceNum === "monitor"; // subscribe-only, no publish
  const userLang      = detectLang();

  useRTCClient(); // keep hook for agora-rtc-react internal state

  const [micOn,        setMicOn]        = useState(!isMonitor); // monitor has no mic
  const [camOn,        setCamOn]        = useState(true);
  const [elapsed,      setElapsed]      = useState(0);
  const [joined,       setJoined]       = useState(false);
  const [spotlightUid, setSpotlightUid] = useState(null);  // which devotee is in Sankalp
  const [allMuted,     setAllMuted]     = useState(false); // priest muted everyone
  const [mutedByPriest,setMutedByPriest]= useState(false); // am I muted by priest?
  const [sankalpToast,    setSankalpToast]    = useState(false); // "Your Sankalp is being performed"
  const [aarti,           setAarti]           = useState(false); // aarti grid mode
  const [handRaised,      setHandRaised]      = useState(false); // devotee raised hand
  const [mutedIndividually,setMutedIndividually] = useState(false); // priest muted just me
  const [sankalpText,     setSankalpText]     = useState(null); // { sanskrit, english, name }
  const [dakshinaActive,  setDakshinaActive]  = useState(false);     // dakshina screen visible
  const [dakshinaVisible, setDakshinaVisible] = useState("private"); // "private" | "all"
  const [dakshinaAmt,     setDakshinaAmt]     = useState(101);       // selected amount
  const [dakshinaCustom,  setDakshinaCustom]  = useState("");        // custom amount input
  const [dakshinaPaid,    setDakshinaPaid]    = useState(false);     // I confirmed payment
  const [paidToasts,      setPaidToasts]      = useState([]);        // group-visible paid names
  const [uidNames,        setUidNames]        = useState({});        // uid → { name, device }
  const [ritualCard,      setRitualCard]      = useState(null);      // current ritual moment card to show
  const [priestPanelOpen, setPriestPanelOpen] = useState(false);     // priest slide-up control tray
  const [dakshinaSent,    setDakshinaSent]    = useState(false);     // priest tracking dakshina overlay state
  const [raisedHands,     setRaisedHands]     = useState(new Set()); // set of uids with raised hands
  const [waitingMusicOn,  setWaitingMusicOn]  = useState(true);      // ambient Om during waiting room

  useEffect(() => {
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const fmtTime = s =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  // ── Ambient Om tone during waiting room (Web Audio API — no external files) ──
  useEffect(() => {
    if (isPriestLocal || hasPriest || !joined || !waitingMusicOn) return;

    let audioCtx = null;
    let oscNodes = [];

    const start = async () => {
      try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtx.state === "suspended") await audioCtx.resume();

        const master = audioCtx.createGain();
        master.gain.setValueAtTime(0, audioCtx.currentTime);
        // Slow 4-second fade-in so it doesn't startle
        master.gain.linearRampToValueAtTime(0.07, audioCtx.currentTime + 4);
        master.connect(audioCtx.destination);

        // 136.1 Hz — OM / Earth-year frequency (sacred in Indian tradition)
        // Add a perfect 5th (×1.5) and octave (×2) for richness
        const FREQS = [136.1, 204.15, 272.2];
        const VOLS  = [0.60,  0.25,   0.15];

        FREQS.forEach((freq, i) => {
          const osc = audioCtx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const g = audioCtx.createGain();
          g.gain.value = VOLS[i];
          osc.connect(g);
          g.connect(master);
          osc.start();
          oscNodes.push(osc);
        });

        // Slow vibrato LFO (0.3 Hz) — subtle living quality to the drone
        const lfo     = audioCtx.createOscillator();
        const lfoGain = audioCtx.createGain();
        lfo.type           = "sine";
        lfo.frequency.value = 0.3;
        lfoGain.gain.value  = 0.9;
        lfo.connect(lfoGain);
        lfoGain.connect(oscNodes[0].frequency); // modulates base tone only
        lfo.start();
        oscNodes.push(lfo);
      } catch {}
    };

    start();

    return () => {
      try {
        oscNodes.forEach(n => { try { n.stop(); } catch {} });
        audioCtx?.close();
      } catch {}
    };
  }, [isPriestLocal, hasPriest, joined, waitingMusicOn]); // eslint-disable-line

  // Monitor device: no mic, no camera published
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(!isMonitor && micOn);
  const { localCameraTrack }     = useLocalCameraTrack(!isMonitor && camOn,
    { encoderConfig: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: 30, bitrateMin: 600, bitrateMax: 2000 } }
  );
  usePublish(isMonitor ? [] : [localMicrophoneTrack, localCameraTrack]);
  const { isLoading } = useJoin({ appid: appId, channel, token, uid }, true);
  const remoteUsers   = useRemoteUsers();

  useEffect(() => { if (!isLoading) setJoined(true); }, [isLoading]);

  // ── Broadcast identity so all participants can label this tile ──────────────
  useEffect(() => {
    if (!joined) return;
    // Small delay to ensure data stream is created first
    const t = setTimeout(() => {
      sendSignal({ type: "identify", uid, name: bookingName, device: deviceNum, isMonitor });
    }, 1500);
    return () => clearTimeout(t);
  }, [joined]); // eslint-disable-line

  // ── Fetch AI sankalpa text (devotees only) ───────────────────────────────────
  useEffect(() => {
    if (isPriestLocal || !booking?.id) return;
    fetch(`${API}/puja/sankalpa/${booking.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setSankalpText(data); })
      .catch(() => {});
  }, [booking?.id, isPriestLocal]); // eslint-disable-line

  // ── Data stream: listen for signals after join ───────────────────────────────
  // SDK 4.20+: no createDataStream — sendStreamMessage takes data directly
  useEffect(() => {
    if (!joined) return;

    const onMessage = (_, data) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(data));
        if (msg.type === "sankalp") {
          setSpotlightUid(msg.uid);
          if (!isPriestLocal && msg.uid === uid) {
            setSankalpToast(true);
            setTimeout(() => setSankalpToast(false), 6000);
          }
        } else if (msg.type === "clear_sankalp") {
          setSpotlightUid(null);
        } else if (msg.type === "mute_all") {
          if (!isPriestLocal) setMutedByPriest(msg.muted);
        } else if (msg.type === "aarti") {
          setAarti(msg.active);
        } else if (msg.type === "mute_devotee" && msg.uid === uid) {
          setMutedIndividually(msg.muted);
          if (!msg.muted) setHandRaised(false); // unmute clears hand raise
        } else if (msg.type === "raise_hand_ack" && msg.uid === uid) {
          setHandRaised(false); // priest acknowledged — clear hand
        } else if (msg.type === "raise_hand" && isPriestLocal) {
          setRaisedHands(prev => {
            const n = new Set(prev);
            if (msg.raised) n.add(msg.uid); else n.delete(msg.uid);
            return n;
          });
        } else if (msg.type === "identify") {
          setUidNames(prev => ({ ...prev, [msg.uid]: { name: msg.name, device: msg.device } }));
        } else if (msg.type === "dakshina") {
          if (!isPriestLocal) {
            setDakshinaActive(msg.active);
            setDakshinaVisible(msg.visible || "private");
          }
        } else if (msg.type === "ritual_moment" && !isPriestLocal) {
          const moment = RITUAL_MOMENTS[msg.momentId];
          if (moment) {
            setRitualCard(moment);
            setTimeout(() => setRitualCard(null), 8000);
          }
        } else if (msg.type === "dakshina_paid" && msg.uid !== uid && !isPriestLocal) {
          // Group-visible mode: show a toast to other devotees
          if (msg.visible === "all" || dakshinaVisible === "all") {
            const toastName = msg.name || "A devotee";
            const toastId   = Date.now();
            setPaidToasts(prev => [...prev, { id: toastId, name: toastName }]);
            setTimeout(() => setPaidToasts(prev => prev.filter(t => t.id !== toastId)), 5000);
          }
        }
      } catch {}
    };
    client.on("stream-message", onMessage);
    return () => client.off("stream-message", onMessage);
  }, [joined, isPriestLocal, uid]); // eslint-disable-line

  // ── Enforce mute when priest mutes everyone or this devotee individually ─────
  useEffect(() => {
    if ((mutedByPriest || mutedIndividually) && micOn) {
      setMicOn(false);
    }
  }, [mutedByPriest, mutedIndividually]); // eslint-disable-line

  // ── Send signal helper ───────────────────────────────────────────────────────
  const sendSignal = (msg) => {
    if (!joined) return;
    try {
      client.sendStreamMessage(new TextEncoder().encode(JSON.stringify(msg)));
    } catch {}
  };

  const handleSankalp = (devoteeUid, devoteeName) => {
    const clearing = spotlightUid === devoteeUid;
    if (clearing) {
      setSpotlightUid(null);
      sendSignal({ type: "clear_sankalp" });
    } else {
      setSpotlightUid(devoteeUid);
      sendSignal({ type: "sankalp", uid: devoteeUid, name: devoteeName });
    }
  };

  const handleMuteAll = () => {
    const next = !allMuted;
    setAllMuted(next);
    sendSignal({ type: "mute_all", muted: next });
  };

  const handleRaiseHand = () => {
    const next = !handRaised;
    setHandRaised(next);
    sendSignal({ type: "raise_hand", uid, raised: next });
  };

  const handleAarti = () => {
    const next = !aarti;
    setAarti(next);
    sendSignal({ type: "aarti", active: next });
  };

  const handleRitualMoment = (momentId) => {
    sendSignal({ type: "ritual_moment", momentId });
    setPriestPanelOpen(false);
  };

  const handleDakshina = (visible = "private") => {
    const next = !dakshinaSent;
    setDakshinaSent(next);
    sendSignal({ type: "dakshina", active: next, visible });
  };

  const handleMuteDevotee = (devoteeUid, currentlyAudible) => {
    sendSignal({ type: "mute_devotee", uid: devoteeUid, muted: currentlyAudible });
  };

  const handleAckHand = (devoteeUid) => {
    setRaisedHands(prev => { const n = new Set(prev); n.delete(devoteeUid); return n; });
    sendSignal({ type: "raise_hand_ack", uid: devoteeUid });
    sendSignal({ type: "mute_devotee", uid: devoteeUid, muted: false });
  };

  useEffect(() => {
    return () => {
      localMicrophoneTrack?.stop(); localMicrophoneTrack?.close();
      localCameraTrack?.stop();     localCameraTrack?.close();
    };
  }, [localMicrophoneTrack, localCameraTrack]);

  const handleLeave = () => {
    localMicrophoneTrack?.stop(); localMicrophoneTrack?.close();
    localCameraTrack?.stop();     localCameraTrack?.close();
    onLeave();
  };

  // ── Identify streams ────────────────────────────────────────────────────────
  // ── Display name for any remote uid ─────────────────────────────────────────
  const tileLabel = (u, fallback) => {
    const info = uidNames[u.uid];
    if (!info) return fallback;
    return info.isMonitor ? `${info.name} — Monitor` : info.name;
  };

  const priestUser   = remoteUsers.find(u => u.uid === priestUid);
  // Filter out priest, silent priest-monitor, and any devotee monitor UIDs (they don't publish video)
  const monitorUids  = new Set(
    Object.entries(uidNames)
      .filter(([, info]) => info.isMonitor)
      .map(([uid]) => Number(uid))
  );
  const devoteeUsers = remoteUsers.filter(
    u => u.uid !== priestUid && u.uid !== monitorUid && !monitorUids.has(u.uid)
  );
  const hasPriest        = !!priestUser;
  const spotlightActive  = !!spotlightUid && !isPriestLocal;
  const spotlitIsMe      = spotlightUid === uid;
  const spotlitRemoteUser = devoteeUsers.find(u => u.uid === spotlightUid) ?? null;
  const otherDevotees    = devoteeUsers.filter(u => u.uid !== spotlightUid);

  const total = 1 + remoteUsers.length;
  const cols  = total <= 1 ? 1 : total <= 2 ? 2 : total <= 4 ? 2 : 3;
  const tileH = total <= 2 ? "calc(100vh - 192px)" : total <= 4 ? "calc(50vh - 104px)" : "calc(33.3vh - 90px)";

  if (isLoading && !joined) {
    return <LoadingRoom pujaName={booking.puja_name} pujaEmoji={booking.puja_emoji} />;
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 1000, display: "flex", flexDirection: "column" }}>
      <style>{`
        @keyframes candle-flicker {
          0%,100% { opacity: 0.85; } 30% { opacity: 1; } 70% { opacity: 0.7; }
        }
        @keyframes sacred-glow {
          0%,100% { box-shadow: 0 0 0 1px rgba(232,131,42,0.3), 0 0 32px rgba(232,131,42,0.08); }
          50%      { box-shadow: 0 0 0 1px rgba(232,131,42,0.5), 0 0 48px rgba(232,131,42,0.15); }
        }
        @keyframes sankalp-pulse {
          0%,100% { box-shadow: 0 0 0 2px #C9953A, 0 0 24px rgba(201,149,58,0.5), 0 0 48px rgba(201,149,58,0.2); transform: scale(1.0); }
          50%      { box-shadow: 0 0 0 3px #E8832A, 0 0 36px rgba(232,131,42,0.7), 0 0 64px rgba(232,131,42,0.3); transform: scale(1.04); }
        }
        @keyframes toast-in {
          from { opacity:0; transform:translateY(12px) scale(0.95); }
          to   { opacity:1; transform:translateY(0)    scale(1); }
        }
        @keyframes spotlight-glow {
          0%,100% { box-shadow: 0 0 0 2px rgba(201,149,58,0.6), 0 0 32px rgba(201,149,58,0.25); }
          50%      { box-shadow: 0 0 0 3px rgba(232,131,42,0.9), 0 0 52px rgba(232,131,42,0.4); }
        }
        @keyframes aarti-flicker {
          0%,100% { opacity: 0.9; box-shadow: 0 0 12px rgba(232,131,42,0.5); }
          40%      { opacity: 1;   box-shadow: 0 0 24px rgba(232,131,42,0.9); }
          70%      { opacity: 0.8; box-shadow: 0 0 8px  rgba(232,131,42,0.4); }
        }
        @keyframes sankalp-rise {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes diya-glow {
          0%,100% { filter: drop-shadow(0 0 12px rgba(232,131,42,0.7)); }
          50%      { filter: drop-shadow(0 0 28px rgba(232,131,42,1.0)); }
        }
        @keyframes dakshina-slide {
          from { opacity: 0; transform: translateY(100%); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes paid-pop {
          0%   { transform: scale(0.8); opacity: 0; }
          60%  { transform: scale(1.08); }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes ritual-card-in {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes ritual-card-out {
          from { opacity: 1; }
          to   { opacity: 0; }
        }
      `}</style>

      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 20px",
        background: "rgba(13,8,24,0.95)", borderBottom: `1px solid ${C.border}`,
        backdropFilter: "blur(12px)", flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "1.5rem", filter: "drop-shadow(0 0 8px rgba(232,131,42,0.5))", animation: "candle-flicker 3s ease-in-out infinite" }}>
            {booking.puja_emoji || "🙏"}
          </span>
          <div>
            <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "0.88rem", color: C.saffron }}>
              {booking.puja_name}
            </div>
            <div style={{ fontSize: "0.7rem", color: C.dim, marginTop: "1px" }}>
              {hasPriest
                ? `🔱 ${priestName || "Priest"}`
                : priestName ? `🔱 ${priestName} — joining…` : "Waiting for priest…"}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: "6px",
            background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.28)",
            borderRadius: "20px", padding: "4px 12px", fontSize: "0.7rem", color: "#86efac",
          }}>
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#22c55e", display: "inline-block", boxShadow: "0 0 6px #22c55e" }} />
            {joined ? "Live" : "Connecting"} · {fmtTime(elapsed)}
          </div>
          <div style={{
            background: "rgba(201,149,58,0.08)", border: `1px solid ${C.border}`,
            borderRadius: "20px", padding: "4px 10px", fontSize: "0.7rem", color: C.dim,
          }}>
            👥 {total}
          </div>
        </div>
      </div>

      {/* ── Video area ───────────────────────────────────────────────────────── */}

      {isMonitor ? (
        /* ══ MONITOR VIEW: subscribe-only — watch priest full screen, full controls ══ */
        <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
          {/* Priest video — full screen */}
          {priestUser ? (
            <RemoteUser
              user={priestUser}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <div style={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: "12px",
            }}>
              <span style={{ fontSize: "3rem", opacity: 0.4 }}>🔱</span>
              <div style={{ fontSize: "0.82rem", color: C.dim, fontStyle: "italic" }}>
                {priestName ? `Waiting for ${priestName}…` : "Waiting for priest…"}
              </div>
            </div>
          )}

          {/* Sankalpa overlay — same as main devotee view */}
          {spotlitIsMe && sankalpText && (
            <div style={{
              position: "absolute", inset: 0, zIndex: 20,
              background: "linear-gradient(160deg, rgba(6,3,10,0.94) 0%, rgba(19,13,34,0.96) 100%)",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              padding: "32px 28px", animation: "sankalp-rise 0.6s ease-out",
            }}>
              <div style={{ fontSize: "2.8rem", marginBottom: "20px", animation: "diya-glow 2s ease-in-out infinite" }}>🪔</div>
              <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "1rem", color: C.gold, textAlign: "center", lineHeight: "1.8", marginBottom: "20px" }}>
                {sankalpText.sanskrit}
              </div>
              <div style={{ fontSize: "0.82rem", color: C.cream, textAlign: "center", lineHeight: "1.7", fontStyle: "italic", opacity: 0.85 }}>
                {sankalpText.english}
              </div>
              <div style={{ marginTop: "28px", fontSize: "0.7rem", color: C.dim, letterSpacing: "0.08em" }}>
                🙏 Read aloud — priest is witnessing
              </div>
            </div>
          )}

          {/* Other devotee tiles — floating bottom strip */}
          {devoteeUsers.length > 0 && (
            <div style={{
              position: "absolute", bottom: "80px", left: "10px", right: "10px",
              display: "flex", gap: "8px", overflowX: "auto",
            }}>
              {devoteeUsers.map(u => (
                <div key={u.uid} style={{ flexShrink: 0, width: "120px", height: "90px", borderRadius: "10px", overflow: "hidden", border: `1px solid ${C.border}` }}>
                  <RemoteUser user={u} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <div style={{ position: "absolute", bottom: "4px", left: "6px", fontSize: "0.6rem", color: C.cream, background: "rgba(6,3,10,0.7)", padding: "1px 6px", borderRadius: "4px" }}>
                    {tileLabel(u, "Devotee")}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Monitor badge */}
          <div style={{
            position: "absolute", top: "14px", right: "14px", zIndex: 5,
            background: "rgba(99,102,241,0.2)", border: "1px solid rgba(99,102,241,0.4)",
            borderRadius: "16px", padding: "4px 12px",
            fontSize: "0.65rem", color: "#a5b4fc", letterSpacing: "0.05em",
          }}>
            📺 Monitor · {fmtTime(elapsed)}
          </div>

          {/* Ritual moment card — same as main devotee view */}
          {ritualCard && (
            <div onClick={() => setRitualCard(null)} style={{
              position: "absolute", bottom: "90px", left: "50%",
              transform: "translateX(-50%)", zIndex: 15,
              background: "linear-gradient(135deg, rgba(13,8,24,0.97), rgba(19,13,34,0.97))",
              border: `1px solid ${C.border}`,
              borderRadius: "16px", padding: "16px 22px",
              maxWidth: "380px", width: "calc(100% - 48px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(201,149,58,0.1)",
              animation: "ritual-card-in 0.4s ease-out",
              cursor: "pointer",
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <span style={{ fontSize: "1.6rem", flexShrink: 0, marginTop: "2px" }}>
                  {ritualCard.emoji}
                </span>
                <div>
                  <div style={{
                    fontFamily: "'Cinzel Decorative', serif",
                    fontSize: "0.72rem", color: C.gold,
                    letterSpacing: "0.05em", marginBottom: "5px",
                  }}>
                    {ritualCard.title[userLang] || ritualCard.title.en}
                  </div>
                  <div style={{
                    fontSize: "0.82rem", color: C.cream,
                    lineHeight: "1.55", fontFamily: "'Crimson Pro', serif",
                  }}>
                    {ritualCard.text[userLang] || ritualCard.text.en}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      ) : isPriestLocal ? (
        /* ══ PRIEST VIEW: grid of devotees + Sankalp buttons ═══════════════════ */
        <div style={{ flex: 1, padding: "10px", overflow: "auto" }}>
          {devoteeUsers.length === 0 ? (
            <div style={{
              height: "100%", display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: "14px",
            }}>
              <div style={{ fontSize: "3rem", animation: "candle-flicker 3s ease-in-out infinite" }}>🙏</div>
              <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "0.85rem", color: C.saffron }}>
                Waiting for devotees to join…
              </div>
            </div>
          ) : (
            <div style={{
              display: "grid",
              gridTemplateColumns: `repeat(${devoteeUsers.length <= 2 ? devoteeUsers.length : 3}, 1fr)`,
              gap: "10px",
            }}>
              {devoteeUsers.map((u, i) => {
                const isSpotlit = spotlightUid === u.uid;
                return (
                  <div key={u.uid} style={{
                    position: "relative", borderRadius: "14px", overflow: "visible",
                    transition: "transform 0.3s",
                    transform: isSpotlit ? "scale(1.04)" : "scale(1)",
                    animation: isSpotlit ? "sankalp-pulse 2s ease-in-out infinite" : "none",
                    zIndex: isSpotlit ? 2 : 1,
                  }}>
                    <VideoTile
                      remoteUser={u}
                      name={tileLabel(u, `Devotee ${i + 1}`)}
                      isPriest={false}
                      isMuted={!u.hasAudio}
                      isCamOff={!u.hasVideo}
                      isLocal={false}
                      style={{ height: "220px", borderRadius: "14px" }}
                    />
                    {/* Sankalp overlay badge */}
                    {isSpotlit && (
                      <div style={{
                        position: "absolute", top: "10px", left: "10px",
                        background: "rgba(201,149,58,0.9)", borderRadius: "8px",
                        padding: "3px 10px", fontSize: "0.65rem",
                        color: "#06030A", fontFamily: "'Cinzel Decorative', serif",
                        letterSpacing: "0.05em",
                      }}>
                        🙏 SANKALP
                      </div>
                    )}
                    {/* Raised hand badge — tap to unmute & acknowledge */}
                    {raisedHands.has(u.uid) && (
                      <button
                        onClick={() => handleAckHand(u.uid)}
                        style={{
                          position: "absolute", top: "10px", right: "10px",
                          background: "rgba(250,204,21,0.9)", border: "none",
                          borderRadius: "8px", padding: "3px 10px",
                          fontSize: "0.65rem", cursor: "pointer",
                          color: "#06030A", fontFamily: "'Cinzel Decorative', serif",
                          letterSpacing: "0.05em", animation: "toast-in 0.3s ease-out",
                        }}
                      >
                        🙋 Unmute
                      </button>
                    )}
                    {/* Individual mute toggle */}
                    {!isSpotlit && (
                      <button
                        onClick={() => handleMuteDevotee(u.uid, u.hasAudio)}
                        title={u.hasAudio ? "Mute this devotee" : "Unmute this devotee"}
                        style={{
                          position: "absolute", bottom: "50px", right: "8px",
                          background: "rgba(13,8,24,0.75)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          borderRadius: "6px", padding: "4px 8px",
                          fontSize: "0.8rem", cursor: "pointer",
                          transition: "background 0.15s",
                        }}
                      >
                        {u.hasAudio ? "🔇" : "🔊"}
                      </button>
                    )}
                    {/* Sankalp button */}
                    <button
                      onClick={() => handleSankalp(u.uid, tileLabel(u, `Devotee ${i + 1}`))}
                      style={{
                        position: "absolute", bottom: "10px", left: "50%",
                        transform: "translateX(-50%)",
                        background: isSpotlit
                          ? "rgba(201,149,58,0.9)"
                          : "rgba(13,8,24,0.85)",
                        border: `1px solid ${isSpotlit ? "#C9953A" : "rgba(201,149,58,0.35)"}`,
                        borderRadius: "8px", padding: "5px 14px",
                        color: isSpotlit ? "#06030A" : C.gold,
                        fontSize: "0.72rem", cursor: "pointer",
                        fontFamily: "'Cinzel Decorative', serif",
                        backdropFilter: "blur(8px)",
                        whiteSpace: "nowrap",
                        transition: "all 0.2s",
                      }}
                    >
                      {isSpotlit ? "End Sankalp" : "Sankalp 🙏"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      ) : hasPriest ? (
        /* ══ DEVOTEE VIEW ════════════════════════════════════════════════════════ */
        <div style={{ flex: 1, position: "relative", padding: "10px", overflow: "hidden", display: "flex", flexDirection: "column" }}>

          {/* Sankalp toast — shown to the spotlighted devotee */}
          {sankalpToast && (
            <div style={{
              position: "absolute", top: "20px", left: "50%",
              transform: "translateX(-50%)", zIndex: 10,
              background: "rgba(201,149,58,0.95)",
              border: "1px solid rgba(232,131,42,0.8)",
              borderRadius: "12px", padding: "12px 24px",
              fontFamily: "'Cinzel Decorative', serif",
              fontSize: "0.82rem", color: "#06030A",
              animation: "toast-in 0.4s ease-out",
              textAlign: "center", boxShadow: "0 0 32px rgba(201,149,58,0.4)",
            }}>
              🙏 Your Sankalp is being performed
            </div>
          )}

          {/* ── Sankalpa text overlay — shown when priest spotlights this devotee ── */}
          {spotlitIsMe && (
            <div style={{
              position: "absolute", inset: 0, zIndex: 20,
              background: "linear-gradient(160deg, rgba(6,3,10,0.94) 0%, rgba(19,13,34,0.96) 100%)",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              padding: "32px 28px",
              animation: "sankalp-rise 0.6s ease-out",
            }}>
              {/* Diya */}
              <div style={{
                fontSize: "3rem", marginBottom: "18px",
                animation: "diya-glow 2s ease-in-out infinite",
              }}>🪔</div>

              {/* Header */}
              <div style={{
                fontFamily: "'Cinzel Decorative', serif",
                fontSize: "0.72rem", letterSpacing: "0.14em",
                color: C.saffron, marginBottom: "22px", textAlign: "center",
              }}>
                YOUR SANKALPA
              </div>

              {sankalpText ? (
                <>
                  {/* Sanskrit text */}
                  <div style={{
                    fontFamily: "'Noto Sans Devanagari', serif",
                    fontSize: "1.25rem", lineHeight: "1.8",
                    color: C.cream, textAlign: "center",
                    marginBottom: "20px",
                    textShadow: "0 0 24px rgba(232,131,42,0.25)",
                    maxWidth: "520px",
                  }}>
                    {sankalpText.sanskrit}
                  </div>

                  {/* Divider */}
                  <div style={{
                    width: "60px", height: "1px",
                    background: "linear-gradient(90deg, transparent, rgba(201,149,58,0.6), transparent)",
                    marginBottom: "18px",
                  }} />

                  {/* English translation */}
                  <div style={{
                    fontSize: "0.9rem", lineHeight: "1.7",
                    color: C.dim, textAlign: "center",
                    fontStyle: "italic", maxWidth: "440px",
                  }}>
                    {sankalpText.english}
                  </div>
                </>
              ) : (
                <div style={{
                  fontSize: "0.88rem", color: C.dim,
                  fontStyle: "italic", textAlign: "center",
                }}>
                  Preparing your sankalpa…
                </div>
              )}

              {/* Instruction */}
              <div style={{
                marginTop: "28px",
                fontSize: "0.72rem", letterSpacing: "0.08em",
                color: "rgba(201,149,58,0.5)",
                fontFamily: "'Cinzel Decorative', serif",
                textAlign: "center",
              }}>
                Read aloud — priest is witnessing
              </div>
            </div>
          )}

          {/* Muted-by-priest banner */}
          {(mutedByPriest || mutedIndividually) && (
            <div style={{
              position: "absolute", top: sankalpToast ? "70px" : "20px", left: "50%",
              transform: "translateX(-50%)", zIndex: 10,
              background: "rgba(13,8,24,0.9)", border: "1px solid rgba(239,68,68,0.4)",
              borderRadius: "10px", padding: "7px 18px",
              fontSize: "0.75rem", color: "#fca5a5",
              display: "flex", alignItems: "center", gap: "7px",
            }}>
              🔇 {mutedIndividually && !mutedByPriest ? "Muted by priest" : "Muted by priest during chanting"}
            </div>
          )}

          {/* Group dakshina toasts — visible when dakshinaVisible === "all" */}
          {paidToasts.length > 0 && (
            <div style={{
              position: "absolute", bottom: "80px", left: "18px", zIndex: 12,
              display: "flex", flexDirection: "column", gap: "6px",
            }}>
              {paidToasts.map(t => (
                <div key={t.id} style={{
                  background: "rgba(34,197,94,0.12)",
                  border: "1px solid rgba(34,197,94,0.35)",
                  borderRadius: "10px", padding: "7px 14px",
                  fontSize: "0.78rem", color: "#86efac",
                  display: "flex", alignItems: "center", gap: "7px",
                  animation: "toast-in 0.3s ease-out",
                }}>
                  🙏 {t.name} offered dakshina
                </div>
              ))}
            </div>
          )}

          {/* ── Ritual moment card — floats bottom-center, auto-dismisses ───── */}
          {ritualCard && (
            <div onClick={() => setRitualCard(null)} style={{
              position: "absolute", bottom: "90px", left: "50%",
              transform: "translateX(-50%)", zIndex: 15,
              background: "linear-gradient(135deg, rgba(13,8,24,0.97), rgba(19,13,34,0.97))",
              border: `1px solid ${C.border}`,
              borderRadius: "16px", padding: "16px 22px",
              maxWidth: "320px", width: "calc(100% - 48px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(201,149,58,0.1)",
              animation: "ritual-card-in 0.4s ease-out",
              cursor: "pointer",
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <span style={{ fontSize: "1.6rem", flexShrink: 0, marginTop: "2px" }}>
                  {ritualCard.emoji}
                </span>
                <div>
                  <div style={{
                    fontFamily: "'Cinzel Decorative', serif",
                    fontSize: "0.72rem", color: C.gold,
                    letterSpacing: "0.05em", marginBottom: "5px",
                  }}>
                    {ritualCard.title[userLang] || ritualCard.title.en}
                  </div>
                  <div style={{
                    fontSize: "0.8rem", color: C.cream,
                    lineHeight: "1.55", fontFamily: "'Crimson Pro', serif",
                  }}>
                    {ritualCard.text[userLang] || ritualCard.text.en}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Hand raised indicator */}
          {handRaised && (
            <div style={{
              position: "absolute", bottom: "18px", left: "18px", zIndex: 10,
              background: "rgba(201,149,58,0.15)", border: "1px solid rgba(201,149,58,0.5)",
              borderRadius: "10px", padding: "7px 16px",
              fontSize: "0.75rem", color: C.gold,
              display: "flex", alignItems: "center", gap: "7px",
              animation: "toast-in 0.3s ease-out",
            }}>
              🙋 Hand raised — waiting for priest
            </div>
          )}

          {aarti ? (
            /* ── AARTI VIEW: devotee grid top + priest large bottom ── */
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px", minHeight: 0 }}>
              {/* Top: all devotee tiles in grid */}
              <div style={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.min(devoteeUsers.length + 1, 4)}, 1fr)`,
                gap: "6px", flexShrink: 0,
              }}>
                <VideoTile
                  isLocal localCameraTrack={localCameraTrack}
                  name="You" isPriest={false} isMuted={!micOn} isCamOff={!camOn}
                  style={{ height: "110px", borderRadius: "10px" }}
                />
                {devoteeUsers.map(u => (
                  <VideoTile
                    key={u.uid} remoteUser={u} name="Devotee"
                    isPriest={false} isMuted={!u.hasAudio} isCamOff={!u.hasVideo} isLocal={false}
                    style={{ height: "110px", borderRadius: "10px" }}
                  />
                ))}
              </div>
              {/* Bottom: priest large */}
              <div style={{ flex: 1, borderRadius: "18px", overflow: "hidden", position: "relative", minHeight: 0 }}>
                <VideoTile
                  remoteUser={priestUser}
                  name={priestName || "Priest"}
                  isPriest
                  isMuted={!priestUser.hasAudio}
                  isCamOff={!priestUser.hasVideo}
                  isLocal={false}
                  style={{ height: "100%", borderRadius: "18px" }}
                />
                <div style={{
                  position: "absolute", top: "16px", left: "50%",
                  transform: "translateX(-50%)", zIndex: 2,
                  background: "rgba(232,131,42,0.92)",
                  borderRadius: "20px", padding: "6px 22px",
                  fontFamily: "'Cinzel Decorative', serif",
                  fontSize: "0.78rem", color: "#06030A",
                  animation: "aarti-flicker 1.8s ease-in-out infinite",
                  letterSpacing: "0.07em", whiteSpace: "nowrap",
                }}>
                  🪔 Aarti Time
                </div>
              </div>
            </div>

          ) : spotlightActive ? (
            /* ── SPLIT VIEW: priest left 60% + spotlighted devotee right 40% ── */
            <>
              <div style={{ flex: 1, display: "flex", gap: "10px", minHeight: 0 }}>
                {/* Priest — left 60% */}
                <div style={{ flex: 6, borderRadius: "18px", overflow: "hidden", animation: "sacred-glow 5s ease-in-out infinite" }}>
                  <VideoTile
                    remoteUser={priestUser}
                    name={priestName || "Priest"}
                    isPriest
                    isMuted={!priestUser.hasAudio}
                    isCamOff={!priestUser.hasVideo}
                    isLocal={false}
                    style={{ height: "100%", borderRadius: "18px" }}
                  />
                </div>
                {/* Spotlighted devotee — right 40% */}
                <div style={{
                  flex: 4, borderRadius: "18px", overflow: "hidden", position: "relative",
                  animation: "spotlight-glow 2s ease-in-out infinite",
                }}>
                  {spotlitIsMe ? (
                    <VideoTile
                      isLocal localCameraTrack={localCameraTrack}
                      name="You" isPriest={false} isMuted={!micOn} isCamOff={!camOn}
                      style={{ height: "100%", borderRadius: "18px" }}
                    />
                  ) : spotlitRemoteUser ? (
                    <VideoTile
                      remoteUser={spotlitRemoteUser} name="Devotee"
                      isPriest={false} isMuted={!spotlitRemoteUser.hasAudio} isCamOff={!spotlitRemoteUser.hasVideo} isLocal={false}
                      style={{ height: "100%", borderRadius: "18px" }}
                    />
                  ) : null}
                  <div style={{
                    position: "absolute", top: "12px", left: "12px", zIndex: 2,
                    background: "rgba(201,149,58,0.92)", borderRadius: "8px",
                    padding: "3px 10px", fontSize: "0.65rem",
                    color: "#06030A", fontFamily: "'Cinzel Decorative', serif", letterSpacing: "0.05em",
                  }}>
                    🙏 SANKALP
                  </div>
                </div>
              </div>
              {/* Bottom strip — all devotees except the spotlighted one */}
              <div style={{ height: "118px", display: "flex", gap: "8px", marginTop: "8px", overflowX: "auto", flexShrink: 0 }}>
                {!spotlitIsMe && (
                  <VideoTile
                    isLocal localCameraTrack={localCameraTrack}
                    name="You" isPriest={false} isMuted={!micOn} isCamOff={!camOn}
                    style={{ width: "148px", height: "110px", borderRadius: "10px", flexShrink: 0 }}
                  />
                )}
                {otherDevotees.map(u => (
                  <VideoTile
                    key={u.uid} remoteUser={u} name="Devotee"
                    isPriest={false} isMuted={!u.hasAudio} isCamOff={!u.hasVideo} isLocal={false}
                    style={{ width: "148px", height: "110px", borderRadius: "10px", flexShrink: 0 }}
                  />
                ))}
              </div>
            </>
          ) : (
            /* ── DEFAULT VIEW: priest full screen, devotees float bottom-right ── */
            <>
              <div style={{ flex: 1, borderRadius: "18px", overflow: "hidden", animation: "sacred-glow 5s ease-in-out infinite", minHeight: 0 }}>
                <VideoTile
                  remoteUser={priestUser}
                  name={priestName || "Priest"}
                  isPriest
                  isMuted={!priestUser.hasAudio}
                  isCamOff={!priestUser.hasVideo}
                  isLocal={false}
                  style={{ height: "100%", borderRadius: "18px" }}
                />
              </div>
              <div style={{
                position: "absolute", bottom: "18px", right: "18px",
                display: "flex", flexDirection: "column", gap: "6px",
                maxHeight: "calc(100% - 36px)", overflowY: "auto",
              }}>
                <VideoTile
                  isLocal localCameraTrack={localCameraTrack}
                  name="You" isPriest={false} isMuted={!micOn} isCamOff={!camOn}
                  style={{ width: "148px", height: "110px", borderRadius: "10px" }}
                />
                {devoteeUsers.map(u => (
                  <VideoTile
                    key={u.uid} remoteUser={u} name="Devotee"
                    isPriest={false} isMuted={!u.hasAudio} isCamOff={!u.hasVideo} isLocal={false}
                    style={{ width: "148px", height: "110px", borderRadius: "10px" }}
                  />
                ))}
              </div>
            </>
          )}

          {/* ── Dakshina payment overlay ───────────────────────────────────────── */}
          {dakshinaActive && !isPriestLocal && (() => {
            const PRESETS = [51, 101, 251, 501];
            const isCustom = !PRESETS.includes(dakshinaAmt);
            const finalAmt = isCustom ? (parseInt(dakshinaCustom, 10) || 0) : dakshinaAmt;
            const upiLink  = priestUpi
              ? `upi://pay?pa=${encodeURIComponent(priestUpi)}&pn=${encodeURIComponent(priestName || "Priest")}&am=${finalAmt}&cu=INR&tn=${encodeURIComponent("Dakshina – " + (booking.puja_name || "Puja"))}`
              : null;

            const handlePay = () => {
              if (upiLink) window.open(upiLink, "_self");
            };

            const handleConfirmPaid = () => {
              setDakshinaPaid(true);
              sendSignal({ type: "dakshina_paid", uid, name: sankalpText?.name || "A devotee", visible: dakshinaVisible });
            };

            return (
              <div style={{
                position: "absolute", inset: 0, zIndex: 25,
                background: "linear-gradient(180deg, rgba(6,3,10,0.5) 0%, rgba(6,3,10,0.97) 30%)",
                display: "flex", flexDirection: "column",
                justifyContent: "flex-end",
                animation: "dakshina-slide 0.5s cubic-bezier(0.16,1,0.3,1)",
              }}>
                <div style={{
                  background: "linear-gradient(160deg, #0D0818, #130D22)",
                  borderRadius: "24px 24px 0 0",
                  border: "1px solid rgba(201,149,58,0.3)",
                  borderBottom: "none",
                  padding: "28px 24px 32px",
                  boxShadow: "0 -8px 48px rgba(0,0,0,0.6)",
                }}>
                  {dakshinaPaid ? (
                    /* ── Paid confirmation ── */
                    <div style={{
                      textAlign: "center", padding: "12px 0 8px",
                      animation: "paid-pop 0.5s ease-out",
                    }}>
                      <div style={{ fontSize: "3.5rem", marginBottom: "12px" }}>🙏</div>
                      <div style={{
                        fontFamily: "'Cinzel Decorative', serif",
                        fontSize: "1rem", color: C.saffron, marginBottom: "8px",
                      }}>Dakshina Offered</div>
                      <div style={{ fontSize: "0.85rem", color: C.dim, lineHeight: "1.6" }}>
                        Your offering has been received.<br />
                        The priest will offer a personal blessing.
                      </div>
                      <div style={{
                        marginTop: "18px", fontSize: "0.78rem",
                        color: "rgba(201,149,58,0.4)", fontFamily: "'Cinzel Decorative', serif",
                        letterSpacing: "0.06em",
                      }}>
                        ॐ तत् सत्
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Header */}
                      <div style={{ textAlign: "center", marginBottom: "22px" }}>
                        <div style={{ fontSize: "0.65rem", letterSpacing: "0.14em", color: C.dim, marginBottom: "6px" }}>
                          OFFER DAKSHINA TO
                        </div>
                        <div style={{
                          fontFamily: "'Cinzel Decorative', serif",
                          fontSize: "1.05rem", color: C.cream,
                        }}>
                          🔱 {priestName || "Priest"}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "rgba(201,149,58,0.5)", marginTop: "4px", fontStyle: "italic" }}>
                          Dakshina is sacred — never an obligation
                        </div>
                      </div>

                      {/* Amount presets */}
                      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                        {PRESETS.map(amt => (
                          <button key={amt} onClick={() => { setDakshinaAmt(amt); setDakshinaCustom(""); }} style={{
                            flex: 1, padding: "12px 4px",
                            background: dakshinaAmt === amt ? "rgba(232,131,42,0.2)" : "rgba(255,255,255,0.04)",
                            border: `1px solid ${dakshinaAmt === amt ? "rgba(232,131,42,0.7)" : "rgba(255,255,255,0.1)"}`,
                            borderRadius: "10px", cursor: "pointer",
                            color: dakshinaAmt === amt ? C.saffron : C.dim,
                            fontSize: "0.92rem", fontFamily: "'Crimson Pro', serif",
                            transition: "all 0.15s",
                          }}>
                            ₹{amt}
                          </button>
                        ))}
                        <button onClick={() => setDakshinaAmt(0)} style={{
                          flex: 1, padding: "12px 4px",
                          background: isCustom ? "rgba(232,131,42,0.2)" : "rgba(255,255,255,0.04)",
                          border: `1px solid ${isCustom ? "rgba(232,131,42,0.7)" : "rgba(255,255,255,0.1)"}`,
                          borderRadius: "10px", cursor: "pointer",
                          color: isCustom ? C.saffron : C.dim,
                          fontSize: "0.82rem",
                          transition: "all 0.15s",
                        }}>
                          Other
                        </button>
                      </div>

                      {/* Custom amount input */}
                      {isCustom && (
                        <div style={{ marginBottom: "12px" }}>
                          <input
                            type="number"
                            placeholder="Enter amount (₹)"
                            value={dakshinaCustom}
                            onChange={e => setDakshinaCustom(e.target.value)}
                            style={{
                              width: "100%", boxSizing: "border-box",
                              background: "rgba(255,255,255,0.05)",
                              border: "1px solid rgba(201,149,58,0.4)",
                              borderRadius: "10px", padding: "12px 16px",
                              color: C.cream, fontSize: "1rem",
                              outline: "none",
                            }}
                          />
                        </div>
                      )}

                      {/* Pay button */}
                      <button
                        onClick={handlePay}
                        disabled={!finalAmt}
                        style={{
                          width: "100%", padding: "15px",
                          background: finalAmt
                            ? "linear-gradient(135deg, #E8832A, #C9953A)"
                            : "rgba(255,255,255,0.06)",
                          border: "none", borderRadius: "12px",
                          color: finalAmt ? "white" : C.dim,
                          fontFamily: "'Cinzel Decorative', serif",
                          fontSize: "0.92rem", cursor: finalAmt ? "pointer" : "default",
                          letterSpacing: "0.04em", marginBottom: "10px",
                          boxShadow: finalAmt ? "0 6px 24px rgba(232,131,42,0.3)" : "none",
                          transition: "all 0.2s",
                        }}
                      >
                        {priestUpi
                          ? `Pay ₹${finalAmt || "—"} via UPI 🙏`
                          : `Offer ₹${finalAmt || "—"} Dakshina 🙏`}
                      </button>

                      {/* Confirm paid + skip row */}
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button onClick={handleConfirmPaid} style={{
                          flex: 1, padding: "11px",
                          background: "rgba(34,197,94,0.1)",
                          border: "1px solid rgba(34,197,94,0.3)",
                          borderRadius: "10px", cursor: "pointer",
                          color: "#86efac", fontSize: "0.82rem",
                        }}>
                          ✓ I have paid
                        </button>
                        <button onClick={() => setDakshinaActive(false)} style={{
                          padding: "11px 18px",
                          background: "transparent",
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: "10px", cursor: "pointer",
                          color: C.dim, fontSize: "0.82rem",
                        }}>
                          Skip
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

      ) : (
        /* ══ WAITING: priest hasn't joined yet ══════════════════════════════════ */
        <div style={{ flex: 1, position: "relative", padding: "10px", overflow: "hidden" }}>
          <div style={{
            height: "100%", borderRadius: "18px",
            background: "radial-gradient(ellipse at center, rgba(232,131,42,0.06) 0%, rgba(6,3,10,0) 70%)",
            border: "1px dashed rgba(232,131,42,0.2)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: "14px",
          }}>
            <div style={{ fontSize: "4rem", filter: "drop-shadow(0 0 24px rgba(232,131,42,0.6))", animation: "candle-flicker 3s ease-in-out infinite" }}>🔱</div>
            <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "0.95rem", color: C.saffron }}>
              Preparing the sacred space…
            </div>
            <div style={{ fontSize: "0.78rem", color: C.dim, fontStyle: "italic" }}>
              {priestName ? `${priestName} will join shortly` : "Priest will join shortly"}
            </div>
            <div style={{ marginTop: "6px", fontSize: "0.82rem", color: "rgba(201,149,58,0.35)", fontFamily: "'Noto Sans Devanagari', serif" }}>
              ॐ नमः शिवाय
            </div>
            {/* Ambient Om indicator + mute toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
              {waitingMusicOn && (
                <div style={{
                  display: "flex", alignItems: "center", gap: "5px",
                  fontSize: "0.68rem", color: "rgba(201,149,58,0.5)",
                  animation: "candle-flicker 3s ease-in-out infinite",
                }}>
                  <span style={{ fontSize: "0.75rem" }}>♪</span> Om chanting
                </div>
              )}
              <button
                onClick={() => setWaitingMusicOn(v => !v)}
                style={{
                  background: "transparent",
                  border: "1px solid rgba(201,149,58,0.2)",
                  borderRadius: "14px", padding: "3px 12px",
                  fontSize: "0.68rem", color: C.dim,
                  cursor: "pointer", letterSpacing: "0.04em",
                }}
              >
                {waitingMusicOn ? "🔇 Mute" : "🔊 Unmute"}
              </button>
            </div>
          </div>
          <div style={{ position: "absolute", bottom: "18px", right: "18px" }}>
            <VideoTile
              isLocal localCameraTrack={localCameraTrack}
              name="You" isPriest={false} isMuted={!micOn} isCamOff={!camOn}
              style={{ width: "148px", height: "110px", borderRadius: "10px" }}
            />
          </div>
        </div>
      )}

      {/* ── Priest control tray (slides up above bottom bar) ────────────────── */}
      {isPriestLocal && priestPanelOpen && (
        <div style={{
          background: "rgba(10,6,20,0.98)", borderTop: `1px solid ${C.border}`,
          backdropFilter: "blur(20px)", flexShrink: 0,
          padding: "18px 20px 12px", overflowY: "auto", maxHeight: "320px",
        }}>
          {/* ── Ritual moment cards ── */}
          <div style={{
            fontSize: "0.66rem", color: C.saffron,
            letterSpacing: "0.1em", fontFamily: "'Cinzel Decorative', serif",
            marginBottom: "10px",
          }}>
            SEND RITUAL CARD TO DEVOTEES
          </div>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
            gap: "8px", marginBottom: "16px",
          }}>
            {Object.entries(RITUAL_MOMENTS).map(([id, m]) => (
              <button key={id} onClick={() => handleRitualMoment(id)} style={{
                background: "rgba(201,149,58,0.07)",
                border: `1px solid ${C.border}`,
                borderRadius: "10px", padding: "10px 6px",
                cursor: "pointer", display: "flex",
                flexDirection: "column", alignItems: "center", gap: "5px",
                transition: "background 0.15s",
              }}>
                <span style={{ fontSize: "1.3rem" }}>{m.emoji}</span>
                <span style={{
                  fontSize: "0.58rem", color: C.dim,
                  textAlign: "center", lineHeight: "1.3",
                }}>
                  {m.title.en}
                </span>
              </button>
            ))}
          </div>

          {/* ── Aarti + Dakshina ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <button onClick={handleAarti} style={{
              background: aarti ? "rgba(232,131,42,0.18)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${aarti ? "rgba(232,131,42,0.55)" : "rgba(255,255,255,0.1)"}`,
              borderRadius: "12px", padding: "14px 8px",
              cursor: "pointer", display: "flex",
              flexDirection: "column", alignItems: "center", gap: "6px",
              transition: "all 0.2s",
            }}>
              <span style={{
                fontSize: "1.6rem",
                animation: aarti ? "aarti-flicker 1.8s ease-in-out infinite" : "none",
              }}>🪔</span>
              <span style={{ fontSize: "0.7rem", color: aarti ? C.saffron : C.dim }}>
                {aarti ? "End Aarti" : "Start Aarti"}
              </span>
            </button>
            <button onClick={() => handleDakshina("private")} style={{
              background: dakshinaSent ? "rgba(52,211,153,0.12)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${dakshinaSent ? "rgba(52,211,153,0.4)" : "rgba(255,255,255,0.1)"}`,
              borderRadius: "12px", padding: "14px 8px",
              cursor: "pointer", display: "flex",
              flexDirection: "column", alignItems: "center", gap: "6px",
              transition: "all 0.2s",
            }}>
              <span style={{ fontSize: "1.6rem" }}>🪙</span>
              <span style={{ fontSize: "0.7rem", color: dakshinaSent ? "#6ee7b7" : C.dim }}>
                {dakshinaSent ? "Hide Dakshina" : "Ask Dakshina"}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* ── Bottom controls ──────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: "12px", padding: "12px 24px",
        background: "rgba(13,8,24,0.95)", borderTop: `1px solid ${C.border}`,
        backdropFilter: "blur(12px)", flexShrink: 0,
      }}>
        {/* Monitor: no mic/cam controls — device publishes nothing */}
        {!isMonitor && (
          <CtrlBtn icon={micOn ? "🎙️" : "🔇"} label={micOn ? "Mute" : "Unmute"} active={micOn} onClick={() => setMicOn(v => !v)} />
        )}
        {!isMonitor && (
          <CtrlBtn icon={camOn ? "📹" : "📵"} label={camOn ? "Stop Cam" : "Start Cam"} active={camOn} onClick={() => setCamOn(v => !v)} />
        )}
        {isPriestLocal && (
          <CtrlBtn
            icon={allMuted ? "🔕" : "🔔"}
            label={allMuted ? "Unmute All" : "Mute All"}
            active={allMuted}
            onClick={handleMuteAll}
          />
        )}
        {isPriestLocal && (
          <CtrlBtn
            icon="🎛️"
            label="Controls"
            active={priestPanelOpen}
            onClick={() => setPriestPanelOpen(v => !v)}
          />
        )}
        {!isPriestLocal && (
          <CtrlBtn
            icon="🙋"
            label={handRaised ? "Lower Hand" : "Raise Hand"}
            active={handRaised}
            onClick={handleRaiseHand}
          />
        )}
        <CtrlBtn icon="🙏" label="Leave" danger onClick={handleLeave} />
      </div>
    </div>
  );
}

// ── Public export — wraps with AgoraRTCProvider ────────────────────────────────
export default function PujaRoom({ booking, agoraConfig, onLeave }) {
  return (
    <AgoraRTCProvider client={client}>
      <RoomInner booking={booking} agoraConfig={agoraConfig} onLeave={onLeave} />
    </AgoraRTCProvider>
  );
}
